import { RiseOutlined } from '@ant-design/icons';
import { Modal, Tooltip, Typography } from 'antd';
import { COND_VENTAS_TIPOS, DATE_FORMAT, DOMICLIO_ORIGEN, FACTOR_CONVERSION_VOLUMEN, GUIAS_TIPOS, LISTA_PRECIOS, SECCIONES_TIPOS, SUBRUBRO_ARTICULOS } from 'constants';
import { AUTH_TOKEN } from "constants/auth";
import { DISTRIBUCIONES_EVENTOS_TIPOS, TIPOS_DOMICILIOS, UNIDADES_MEDIDAS } from "constants/demoData";
import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import moment from "moment/moment";

dayjs.locale("es");
dayjs.extend(utc);

export const TOKEN = localStorage.getItem(AUTH_TOKEN)

export const generateElements = (length, fn) => {
  return Array.from({ length: length }).map(() => fn())
};

export const getRandomNumber = (min, max) => {
  return parseInt(Math.random() * (max - min) + min);
};

export const formatLocale = (value) => {
  return value?.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 10 });
};

export const formatCurrency = (value) => {
  if (!value) return `$${formatLocale(0)}`;
  const fixed = Number(value)?.toFixed(2);
  return `$${formatLocale(Number(fixed))}`;
};

export const formatPercentage = (value) => {
  if (!value) return `${formatLocale(0)}%`;
  const fixed = Number(value)?.toFixed(2);
  return `${formatLocale(Number(fixed))}%`
};

export const formatQuantity = (value) => {
  if ((!value || value === 0)) return 0;
  return `${formatLocale(value ?? 0)}`
};

export const formatDuration = (value) => {
  if (!value) return "";

  let totalMinutes = 0;

  if (value.includes("T")) {
    // Formato ISO: "0001-01-01T00:30:00Z"
    const base = new Date("0001-01-01T00:00:00Z");
    const fecha = new Date(value);
    const diffMs = fecha.getTime() - base.getTime();
    totalMinutes = Math.floor(diffMs / 60000);
  } else if (value.includes(":")) {
    // Formato HH:mm
    const [horasStr, minutosStr] = value.split(":");
    const horas = parseInt(horasStr, 10) || 0;
    const minutos = parseInt(minutosStr, 10) || 0;
    totalMinutes = horas * 60 + minutos;
  }

  const horas = Math.floor(totalMinutes / 60);
  const minutos = totalMinutes % 60;

  const partes = [];
  if (horas) partes.push(`${horas} hora${horas !== 1 ? "s" : ""}`);
  if (minutos) partes.push(`${minutos} minuto${minutos !== 1 ? "s" : ""}`);

  return partes.length ? partes.join(" y ") : "0 minutos";
};

export const formatDate = (value, format = DATE_FORMAT) => {
  if (!value || value === "0001-01-01T00:00:00Z") return '';

  const hasOffset = /Z|[+-]\d{2}:\d{2}$/.test(value);

  if (hasOffset) {
    return dayjs(value).utc(false).format(format);
  } else {
    return dayjs.tz(value, "America/Argentina/Buenos_Aires").format(format);
  }
};

export const formatHour = (values) => {
  const hora = dayjs(values).format('HH:mm');
  return dayjs(`1970-01-01T${hora}:00Z`).toISOString();
};

export const formatUnidad = (selectedUnidadMedida, value) => {
  const result = selectedUnidadMedida?.id === UNIDADES_MEDIDAS.VOLUMEN ? value / FACTOR_CONVERSION_VOLUMEN : value;
  if (selectedUnidadMedida?.abreviatura) {
    return selectedUnidadMedida?.abreviatura.replace("@", formatQuantity(result));
  } else {
    return `${formatQuantity(result)} ${selectedUnidadMedida?.suffix}`;
  }
};

export const cleanText = (str) => str?.trim().replace(/\s+/g, ' ');

export const parseCurrencyToDB = (valorFormateado) => {
  if (valorFormateado == null) return null;

  let str = String(valorFormateado).trim();
  str = str.replace(/[^0-9,.-]/g, "");

  const [entera, decimal] = str.split(",");

  const enteraLimpia = entera.replace(/\./g, "").replace(/,/g, "");

  const resultado = decimal
    ? `${enteraLimpia}.${decimal}`
    : enteraLimpia;

  const num = parseFloat(resultado);
  return isNaN(num) ? null : Math.round((num + Number.EPSILON) * 100) / 100;
};

export const sumarDuraciones = (duraciones) => {
  if (!Array.isArray(duraciones)) return "00:00";

  const totalMin = duraciones.reduce((acc, d) => {
    if (typeof d !== "string" || !d.includes("T")) return acc;

    // Extraer solo la parte de la hora: "00:30:00" de "0001-01-01T00:30:00Z"
    const horaCompleta = d.split("T")[1]?.split("Z")[0];
    if (!horaCompleta) return acc;

    const [h, m] = horaCompleta.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return acc;

    return acc + h * 60 + m;
  }, 0);

  const horasTotales = Math.floor(totalMin / 60);
  const minutosTotales = totalMin % 60;

  return `${String(horasTotales).padStart(2, "0")}:${String(minutosTotales).padStart(2, "0")}`;
};

export const getListaTitulo = (articulo, data) => {
  return articulo + ' Desde ' + data.km + ' Km. - Desde ' + currency(data.kg) + ' kg.';
};

export const showErrorModal = ({
  title,
  err,
  ...props
}) => {
  Modal.error({
    title: title || "Error",
    content: (err && typeof err === "string") ? err : err?.message || JSON.stringify(err),
    ...props,
  });
};

export const capitalizeFirstLetter = (string) => {
  return string ? string?.charAt(0).toUpperCase() + string?.slice(1).toLowerCase() : "";
};

export const lowerAndCapitalizeFirstLetter = (string) => {
  if (!string || typeof string !== 'string') return '';
  return string
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const validarUsuario = (e) => {
  let text = e.target.value;
  text = text.toLowerCase().replace(/\s/g, ''); // Sin espacios
  text = text.replace(/[^a-z0-9]/g, ''); // Permitir letras y números
  e.target.value = text;
};

export const normalizarTexto = (e, toUpper = true, allow = [""]) => {
  const input = e.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  let texto = input.value;

  // Quitar tildes
  let sinTildes = texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Convertir a mayúsculas si se pidió
  if (toUpper) sinTildes = sinTildes.toUpperCase();

  // Construir regex dinámico para caracteres permitidos
  const allowStr = allow.join("").replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  const regex = new RegExp(`[^A-Za-z0-9Ññ\\s${allowStr}]`, "g");

  // Eliminar caracteres no permitidos
  let textoLimpio = sinTildes.replace(regex, "");

  // Capitalizar la primera letra (sin tocar las demás)
  if (textoLimpio.length > 0) {
    textoLimpio =
      textoLimpio.charAt(0).toUpperCase() + textoLimpio.slice(1);
  }

  // Calcular desplazamiento del cursor
  const desplazamiento = texto.length - textoLimpio.length;

  input.value = textoLimpio;

  // Restaurar posición del cursor
  input.setSelectionRange(start - desplazamiento, end - desplazamiento);
};

export const validarNumero = e => {
  // Eliminar cualquier carácter que no sea un número
  e.target.value = e.target.value.replace(/[^0-9]/g, '');
};

export const eliminarTildes = text => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, "")
};

export const mayuscula = (e) => {
  const input = e.target;
  const start = input.selectionStart; // Guarda la posición actual del cursor
  const end = input.selectionEnd;

  let texto = input.value;
  const enMayusculas = texto.toUpperCase();
  const sinTildes = eliminarTildes(enMayusculas);

  input.value = sinTildes;

  // Restaurar la posición del cursor
  input.setSelectionRange(start, end);
};

export const alfaNumerico = (e, allowSimbols = [""]) => {
  const input = e.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  let texto = input.value;

  // Construir la expresión regular dinámicamente con los caracteres permitidos
  const allowStr = allowSimbols.join('').replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // Escapar caracteres especiales
  const regex = new RegExp(`[^a-zA-Z0-9Ññ\\s${allowStr}]`, 'g');

  // Reemplazar los caracteres no permitidos
  const nuevoTexto = texto.replace(regex, '');

  // Calcular el desplazamiento del cursor
  const desplazamiento = texto.length - nuevoTexto.length;

  input.value = nuevoTexto;

  // Restaurar la posición del cursor respetando la selección
  input.setSelectionRange(start - desplazamiento, end - desplazamiento);
};

export const filterOption = (inputValue, option) => {
  const parsedOption = eliminarTildes(option.label.toLowerCase())
  const parsedInput = eliminarTildes(inputValue.normalize().toLowerCase())
  return parsedOption.includes(parsedInput)
};

export const filterTransferOption = (inputValue, option) => {
  const parsedOption = option.title.toLowerCase()
  const parsedInput = inputValue.normalize().toLowerCase()
  return parsedOption.includes(parsedInput)
};

export const options = (method, body, idempotencyKey) => ({
  method,
  headers: {
    "Authorization": "Bearer " + localStorage.getItem(AUTH_TOKEN),
    "Content-Type": "application/json",
    ...(method === "POST" && idempotencyKey && {
      "Idempotency-Key": idempotencyKey,
    }),
  },
  body: JSON.stringify(body)
});

export const validarCuit = (cuit) => {
  if (cuit.length !== 11) {
    return false;
  }
  var acumulado = 0;
  var digitos = cuit.split("");
  var digito = parseInt(digitos.pop());
  for (var i = 0; i < digitos.length; i++) {
    acumulado += digitos[9 - i] * (2 + (i % 6));
  }
  var verif = 11 - (acumulado % 11);
  if (verif === 11) {
    verif = 0;
  } else if (verif === 10) {
    verif = 9;
  }
  return digito === verif;
};

// export const agregarPrefijo = (numero) => {
//   if (!numero || numero.length === 0) return '';
//   if (numero.startsWith("54")) {
//     numero = "+" + numero;
//   } else if (!numero.startsWith("+54")) {
//     numero = "+54" + numero;
//   }
//   return numero;
// };

export const agregarPrefijo = (numero) => {
  if (!numero || numero.length === 0) return '';

  // Eliminar cero inicial si está presente
  if (numero.startsWith("0")) {
    numero = numero.slice(1);
  }

  // Agregar prefijo
  if (numero.startsWith("54")) {
    numero = "+" + numero;
  } else if (!numero.startsWith("+54")) {
    numero = "+54" + numero;
  }

  return numero;
};

export const getDomicilio = (cliente, cliente_domicilio) => {
  const result = [];

  if (cliente_domicilio?.origen === TIPOS_DOMICILIOS.CLIENTE) { // Domicilio del cliente
    if (cliente.domiciliocomercial !== "") {
      result.push(cliente.domiciliocomercial);
    }
    if (cliente.localidad !== "") {
      result.push(", " + cliente.localidad);
    }
    if (cliente.codigopostal !== "") {
      result.push(" (" + (cliente.codigopostal)?.trim() + ")");
    }
  } else {
    if (cliente_domicilio?.calle !== "") {
      result.push(cliente_domicilio?.calle + " ");
    }
    if (cliente_domicilio?.altura !== "") {
      result.push(cliente_domicilio?.altura + ", ");
    }
    if (cliente_domicilio?.piso !== "") {
      result.push("Piso " + cliente_domicilio?.piso + ", ");
    }
    if (cliente_domicilio?.depto !== "") {
      result.push("Depto " + cliente_domicilio?.depto + ", ");
    }
    if (cliente_domicilio?.localidad.nombre !== "") {
      result.push(cliente_domicilio?.localidad.nombre);
    }
    if (cliente_domicilio?.localidad.cp !== "") {
      result.push(" (" + cliente_domicilio?.localidad.cp + ")");
    }
    if (cliente_domicilio?.localidad.provincia.nombre !== "") {
      result.push(", " + cliente_domicilio?.localidad.provincia.nombre);
    }
  }

  return result.join("");
};

export const currency = (num) => {
  if (isNaN(num)) {
    num = 0;
  }
  num = parseFloat(num);
  if (num !== "undefined" && num !== null) {
    return num.toFixed(2).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
  } else {
    return '-';
  }
};

export const labelRemitos = (e, filialCobanza, filialFacturacion) => {
  if (e) {
    return 'Filial de cobranza: ' + e?.cliente_ctacte?.filial?.nombre + ' - Filial de facturación: ' + e?.filial?.nombre;
  }
  else if (filialCobanza && filialFacturacion) {
    return 'Filial de cobranza: ' + filialCobanza + ' - Filial de facturación: ' + filialFacturacion;
  }
  else {
    return ""
  }
};

export const onlyNumbers = (event) => {
  const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '.', 'Backspace', 'Delete'];
  const char = event.key;

  // Check for allowed keys or valid comma/point placement:
  const currentValue = event.target.value;
  if ((char === ',' && currentValue.endsWith(' ')) ||
    (char === '.' && !currentValue.includes('.')) ||
    (allowedKeys.includes(char))) {
    // Allow input
  } else {
    event.preventDefault();
  }
};

export const getContactoPrincipal = (cliente) => {
  if (cliente && cliente.clientes_domicilios) {
    const domicilios = cliente.clientes_domicilios.find(e => e.principal === true && e.origen === TIPOS_DOMICILIOS.CLIENTE);
    if (domicilios && domicilios.clientes_domicilios_contactos) {
      const domicilios_contactos = domicilios.clientes_domicilios_contactos.find(e => e.principal === true);
      if (domicilios_contactos && domicilios_contactos.clientes_domicilios_contactos_props) {
        const domicilios_contactos_props = domicilios_contactos.clientes_domicilios_contactos_props.find(e => e.principal === true);
        if (domicilios_contactos_props) {
          return domicilios_contactos_props;
        }
      }
    }
  }

  return null;
};

export const pathIncludes = (e) => {
  return window.location.pathname.includes(e);
};

export const removeLeadingZero = (phoneNumber) => {
  if (phoneNumber.startsWith('0')) {
    return phoneNumber.slice(1);
  }
  return phoneNumber;
};

export const renderVencimiento = (vencimiento) => {
  if (vencimiento === null) {
    return 'Sin vencimiento';
  }
  
  const hoy = new Date().toLocaleDateString('en-CA'); // formato "YYYY-MM-DD"
  const fechaVencimiento = vencimiento.slice(0, 10);

  if (fechaVencimiento < hoy) {
    return (
      <Tooltip title="Vencido" mouseLeaveDelay={0}>
        <Typography.Text style={{ color: '#dc4446' }}>
          {formatDate(fechaVencimiento)}
        </Typography.Text>
      </Tooltip>
    );
  }

  return formatDate(fechaVencimiento);
};

export const disabledDateVencimiento = (current) => {
  return current && current < moment().startOf('day');
};

export const rounded = (num) => {
  return Number(num?.toFixed(2));
};

export const removeDuplicates = (data, uniqueProperty) => {
  return data.reduce((unique, item) => {
    if (!unique.some(e => e[uniqueProperty] === item[uniqueProperty])) {
      unique.push(item);
    }
    return unique;
  }, []);
}

export const viewPDF = async (response, returnUrl = false) => {
  const buf = await response.arrayBuffer();
  const file = await new File([buf], "id" + '.pdf', { type: 'application/pdf' })
  var blob = new Blob([file], { type: "application/pdf" });
  var objectURL = window.URL.createObjectURL(blob);

  if (returnUrl) {
    return objectURL;
  }
  window.open(objectURL);
};

export const viewFile = async (response, fileName, mimeType) => {
  const buf = await response.arrayBuffer();
  const file = new File([buf], fileName, { type: mimeType });
  const blob = new Blob([file], { type: mimeType });
  const objectURL = window.URL.createObjectURL(blob);
  window.open(objectURL, "_blank");
};

export const getColorByEventos = (visitas) => {
  if (!visitas || visitas.length === 0) return 'red';

  const ultimaVisita = visitas[visitas.length - 1];

  switch (ultimaVisita.evento) {
    case DISTRIBUCIONES_EVENTOS_TIPOS.ENTREGA:
      return 'green';
    case DISTRIBUCIONES_EVENTOS_TIPOS.RETIRO:
      return 'magenta';
    case DISTRIBUCIONES_EVENTOS_TIPOS.CARGA_PICKUP:
      return 'blue';
    case DISTRIBUCIONES_EVENTOS_TIPOS.DESCARGA_PICKUP:
      return 'yellow';
    case DISTRIBUCIONES_EVENTOS_TIPOS.AVISO_VISITA:
      return 'gray';
    default:
      return 'red';
  }
};

export const getBasePath = (path, param) => {
  if (!path || path === "/") return "/";

  const paramValues = Object.values(param).map(String);

  if (paramValues.length > 0) {
    const parts = path.split("/").filter(Boolean);

    // Encontrar el índice del segmento que coincide con algún valor de param
    const dynamicIndex = parts.findIndex(p => paramValues.includes(p));

    if (dynamicIndex !== -1) {
      return "/" + parts.slice(0, dynamicIndex).join("/");
    }
  }

  return path.replace(/\/$/, "");
};

export const getRandomHexColor = () => {
  return `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0')}`;
};

// Para datepicker.rangepicker
export const disabledMonthsDate = (months = 2) => (current, { from, type }) => {
  const getYearMonth = (date) => date.year() * 12 + date.month();
  if (from) {
    const minDate = from.clone().subtract(months, 'months');
    const maxDate = from.clone().add(months, 'months');
    switch (type) {
      case 'year':
        return current.year() < minDate.year() || current.year() > maxDate.year();
      case 'month':
        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        );
      default:
        return current.isBefore(minDate, 'day') || current.isAfter(maxDate, 'day');
    }
  }
  return false;
};

export const renderAumento = (porcentaje) => {
  if (porcentaje) {
    return (
      <Tooltip title={`Aumentó ${formatPercentage(porcentaje)}`} mouseLeaveDelay={0}>
        <RiseOutlined style={{ color: '#3DAA18', fontSize: 16 }} />
      </Tooltip>
    );
  } else {
    return null;
  }
}

export const getAccesosIds = (accesos) => {
  const accesosIds = new Set(accesos?.map(a => a.accesos_id));
  return accesosIds;
}

export const checkAcceso = (accesosIds, accesosToCheck) => {
  const lista = Array.isArray(accesosToCheck) ? accesosToCheck : [accesosToCheck];
  return lista.some(id => accesosIds.has(id));
};

// Función auxiliar para construir el objeto de permisos con seguridad
const buildPermisos = (source, isFull = false) => ({
  alta: isFull || (source?.alta ?? false),
  baja: isFull || (source?.baja ?? false),
  modificacion: isFull || (source?.modificacion ?? false),
  consulta: isFull || (source?.consulta ?? false),
  total: isFull || (source?.total ?? false),
  denegado: !isFull && !(source?.consulta ?? false), // Denegado es lo opuesto a tener consulta o total
});

export const resolvePermisos = (usuario, elemento, identificador, basePath) => {
  // 1. Casos de acceso total
  if (usuario?.super) return buildPermisos(null, true);
  // Asumo que tienes una lista de rutas sin autenticación.
  // if (EXCLUSIVE_URL.includes(basePath)) return buildPermisos(null, true); 

  const accionesPermitidas = usuario?.accesos?.flatMap(acceso => acceso) || [];

  // 2. Búsqueda de permisos específicos
  const permiso = accionesPermitidas.find(accion => {
    switch (elemento) {
      case "view":
        // Aquí el identificador es la clave de acceso de la vista (basePath)
        return accion?.acceso?.acceso === identificador && accion?.consulta;
      case "tab":
      case "button":
        // Aquí el identificador es la clave del tab/botón (accesos_id)
        return accion?.accesos_id === identificador && accion?.consulta;
      default:
        return false;
    }
  });

  // 3. Devolver el objeto de permisos
  return buildPermisos(permiso);
};

/**
 * Funcion para crear el objeto GUIA para enviar al backend, values son los valores que obtenemos del formulario
 * @param {*} values 
 * @returns {Object} objeto guia
 */
export const requestGuia = (values) => {
  // const domicilioCliente = values?.cliente?.clientes_domicilios?.filter((domicilio) => domicilio.origen == 2 && domicilio.principal == true)[0]
  const { credito, envase, localidad } = values;
  const tipoEntrega = (entrega) => {
    // values.domicilio_destino.entrega_domicilio_id || values.domicilio_destino.retiro_sucursal_id,
    if (entrega?.entrega_domicilio_id) {
      return DOMICLIO_ORIGEN.DOMICILIO
    }
    if (entrega?.retiro_sucursal_id) {
      return DOMICLIO_ORIGEN.PUNTO_ENTREGA
    }
    return null
  }
  const detalle = []

  //DETALLE DEL CREDITO DEL CLIENTE
  detalle.push({
    articulos_id: credito.articulos_id,
    descripcion: credito.articulo,
    cantidad: 1,
    observaciones: 'CREDITO PREPAGO',
    precios_id: LISTA_PRECIOS.PREPAGO, //este es el id precio determinado para bolsas pack credifin
    // neto_real: ,
    neto: parseFloat(credito.precio),
    // seguro: ,
    subrubros_id: SUBRUBRO_ARTICULOS.SUBRUBRO_ARTI_VENTA_PREPAGO,
  })

  //DETALLE DE LA BOLSA DEL CLIENTE
  if (values?.envaseUpgrade) {
    detalle.push({
      articulos_id: values?.envaseUpgrade.articulos_id,
      descripcion: values?.envaseUpgrade.descripcion,
      cantidad: 1,
      observaciones: 'BOLSA DE SEGURIDAD',
      precios_id: LISTA_PRECIOS.BOLSAS, //este es el id precio determinado para bolsas pack credifin
      // neto_real: ,
      neto: parseFloat(values?.envaseUpgrade.precio),
      // seguro: ,
      envase: values?.envaseUpgrade?.envase,
      subrubros_id: SUBRUBRO_ARTICULOS.SUBRUBRO_ARTI_BOLSAS_DE_SEGURIDAD,

    })
  } else {
    detalle.push({
      articulos_id: envase.articulos_id,
      descripcion: envase.descripcion,
      cantidad: 1,
      observaciones: 'BOLSA DE SEGURIDAD',
      precios_id: LISTA_PRECIOS.BOLSAS, //este es el id precio determinado para bolsas pack credifin
      // neto_real: ,
      neto: parseFloat(envase.precio),
      // seguro: ,
      envase: envase.envase,
      subrubros_id: SUBRUBRO_ARTICULOS.SUBRUBRO_ARTI_BOLSAS_DE_SEGURIDAD,
    })
  }

  const detalle_bulto = []

  if (values?.envaseUpgrade?.envase) {
    detalle_bulto.push({
      envase: envase.envase || values?.envaseUpgrade?.envase,
    })
  } else {
    detalle_bulto.push({
      envase: envase.envase,
    })
  }

  // const comprobantes = values?.remitos?.map((comprobante) => {
  //   return {
  //     valor: `${comprobante.guia}-${comprobante.punto}`,
  //     campo: comprobante.tipo_comprobante,
  //   }
  // })
  const cliente = {
    clientes_id: values?.cliente?.id,
    documento: values?.cliente?.documento,
    razon_social: values?.cliente?.razonsocial,
    posfical: values?.cliente?.posfiscal?.descripcion,
    posfiscal_id: values?.cliente?.posfiscal?.id,
    domicilio: values?.cliente?.domiciliocomercial,
    localidades_id: values?.localidad_id, //este valor no se obtienen del procedimiento(caundo es destinatario)
    filiales_id: values?.filial?.id,
  }
  // console.log(values.remitente)
  const remitente = {
    oclientes_id: values?.remitente?.id,
    odocumento: values?.remitente?.dni || values?.remitente?.cuit || values?.remitente?.documento,
    odomicilios_id: values?.remitente?.domicilio?.value,
    onombre: values?.remitente?.nombrecomercial,
    ocalle: values?.remitente?.domicilio?.label,
    // oaltura: values.remitente.domicilio.altura,
    // opiso: values.remitente.domicilio.piso,
    // odepto: values.remitente.domicilio.depto,
    olocalidad: values?.remitente?.domicilio?.localidad?.nombre,
    olocalidades_id: values?.remitente?.domicilio?.localidad?.id,
    otelefono: values?.remitente?.celular || values?.remitente?.telefono,
    omail: values?.remitente?.email,
    oprovincias_id: values?.remitente?.domicilio?.localidad?.provincia?.id,
    oprovincia: values?.remitente?.domicilio?.localidad?.provincia?.nombre,
  }
  // console.log(values.destinatario)
  const destinatario = {
    dclientes_id: values.destinatario.id,
    dnombre: values.destinatario.nombrecomercial,
    ddocumento: values.destinatario.dni || values.destinatario.cuit || values.destinatario.documento,
    ddomicilios_id: values.destinatario.domicilio.id,
    dcalle: values.destinatario.domicilio.calle,
    daltura: values.destinatario.domicilio.altura,
    dpiso: values.destinatario.domicilio.piso,
    dlocalidad: localidad?.localidad_nombre,
    dlocalidades_id: localidad?.id,
    // cambiar por => values.destinatario.domicilio.dtelefono cuando el procedimeinto se complete
    dtelefono: values.destinatario.celular || values.destinatario.telefono,
    // cambiar por => values.destinatario.domicilio.email cuando el procedimiento se complete
    dmail: values.destinatario.email,
    dprovincia: localidad?.provincia_nombre,
    dprovincias_id: localidad?.provincias_id,
    dlatitud: localidad?.dlatitud,
    dlongitud: localidad?.dlongitud,
  }

  const guia = {
    ofiliales_id: values?.filial?.id,
    dfiliales_id: values?.destinatario?.domicilio?.filiales_id,
    guias_tipos_id: GUIAS_TIPOS.ENVIO_CREDITO_PREPAGO,
    // vd_total: detalle?.reduce((acc, item) => acc + (item?.valor_declarado), 0),
    neto: detalle?.reduce((acc, item) => acc + parseFloat(item?.neto), 0),

    ...cliente,
    ...remitente,
    ...destinatario,

    guias_detalles: detalle,
    // guias_detalles_bultos: detalle_bulto,
    // guias_comprobantes: comprobantes,
    condicion_venta_id: COND_VENTAS_TIPOS.CONTADO,
    secciones_id: SECCIONES_TIPOS.SECCION_ENVIOS_PREPAGO,

    entregas_id: tipoEntrega(values.domicilio_destino),
  }

  return {
    guia,
  }
}

export const calcularPorcentajeAcumulado = (aumentos) => {
  const total = aumentos?.reduce((acc, item) => {
    return acc * (1 + (item.porcentaje || 0) / 100);
  }, 1) - 1;

  return total * 100;
};